import { useEffect, useState } from "react";
import { ZoomIn, ZoomOut, RotateCw, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const fmtKb = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

// Super Admin only: how the stored photo was produced (browser crop/resize or server backstop) and its size.
function OptimisationCaption({ doc }) {
  const o = doc.optimisation;
  if (!o) return null;
  const label = o.cropped ? "Cropped & optimised" : o.cropped === false ? "Optimised (no crop)" : "Optimised";
  const before = o.input_bytes && o.input_bytes > doc.size ? ` (was ${fmtKb(o.input_bytes)})` : "";
  return <span className="hidden sm:inline text-[11px] text-emerald-300/90 whitespace-nowrap" title={`${o.pipeline || ""}${o.client?.reason ? ` · ${o.client.reason}` : ""}`} data-testid="doc-optimisation-caption">{label} · {fmtKb(doc.size)}{before}</span>;
}

export default function DocumentViewer({ documents }) {
  const { user } = useAuth();
  const allOriginals = documents.filter((d) => d.kind === "original");
  const processed = documents.filter((d) => d.kind === "processed");
  const [showOriginal, setShowOriginal] = useState(false);
  // cropped & flattened scan by default (camera or photo upload); the untouched photo stays one tap away
  const originals = processed.length && !showOriginal ? processed : allOriginals;
  const [idx, setIdx] = useState(0);
  const [url, setUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const doc = originals[idx];

  useEffect(() => {
    if (!doc) return;
    let objectUrl;
    api.get(`/invoices/documents/${doc.id}/file`, { responseType: "blob" }).then((r) => {
      objectUrl = URL.createObjectURL(r.data);
      setUrl(objectUrl);
    }).catch(() => setUrl("error"));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [doc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!doc) return <div className="p-6 text-sm text-slate-500" data-testid="no-document">No document available. If this scan was merged into another invoice, this entry is a leftover — delete it from the menu above.</div>;
  const isPdf = doc.content_type === "application/pdf";

  return (
    <div className="flex flex-col h-full bg-slate-900 rounded-lg overflow-hidden" data-testid="document-viewer">
      <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-950 text-slate-300 text-xs">
        <span className="font-mono truncate flex-1 px-1">{doc.filename}</span>
        {user?.is_superadmin && <OptimisationCaption doc={doc} />}
        {processed.length > 0 && <button onClick={() => { setShowOriginal((v) => !v); setIdx(0); }} className={`px-2 py-0.5 rounded-full border text-[11px] font-semibold ${showOriginal ? "border-slate-500 text-slate-200" : "border-emerald-500 text-emerald-300"}`} title={showOriginal ? "Showing the untouched photo" : "Showing the cropped scan"} data-testid="doc-toggle-original">{showOriginal ? "Original photo" : "Cropped"}</button>}
        {originals.length > 1 && (
          <>
            <button onClick={() => setIdx((i) => Math.max(0, i - 1))} className="p-1.5 hover:text-white" data-testid="doc-prev"><ChevronLeft className="h-4 w-4" /></button>
            <span className="font-mono">{idx + 1}/{originals.length}</span>
            <button onClick={() => setIdx((i) => Math.min(originals.length - 1, i + 1))} className="p-1.5 hover:text-white" data-testid="doc-next"><ChevronRight className="h-4 w-4" /></button>
          </>
        )}
        {!isPdf && (
          <>
            <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="p-1.5 hover:text-white" data-testid="doc-zoom-out"><ZoomOut className="h-4 w-4" /></button>
            <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="p-1.5 hover:text-white" data-testid="doc-zoom-in"><ZoomIn className="h-4 w-4" /></button>
            <button onClick={() => setRot((r) => (r + 90) % 360)} className="p-1.5 hover:text-white" data-testid="doc-rotate"><RotateCw className="h-4 w-4" /></button>
          </>
        )}
        {url && url !== "error" && <a href={url} download={doc.filename} className="p-1.5 hover:text-white" data-testid="doc-download"><Download className="h-4 w-4" /></a>}
      </div>
      <div className="flex-1 overflow-auto min-h-[320px]">
        {!url ? <p className="p-6 text-sm text-slate-400">Loading original…</p> : url === "error" ? <p className="p-6 text-sm text-rose-300">Could not load the original document.</p> : isPdf ? (
          <iframe title="Invoice PDF" src={url} className="w-full h-full min-h-[70vh] bg-white" data-testid="doc-pdf-frame" />
        ) : (
          <div className="p-3 grid place-items-start min-h-full">
            <img src={url} alt="Original invoice" style={{ transform: `scale(${zoom}) rotate(${rot}deg)`, transformOrigin: "top left", transition: "transform .15s" }} className="max-w-full" data-testid="doc-image" />
          </div>
        )}
      </div>
    </div>
  );
}
